/**
 * Settings Cache Service
 *
 * Handles persistence of user settings and skill levels
 */

import { settingsStorage } from '../storage/localForage';
import type { UserSkills, AppSettings, StructureBonuses } from '@/types';

/**
 * Default application settings
 */
const DEFAULT_SETTINGS: AppSettings = {
  theme: 'dark',
  language: 'en',
  autoRefreshCache: false,
  cacheExpiryDays: 7,
};

/**
 * Settings cache service
 */
export class SettingsCacheService {
  // ============================================================================
  // User Skills
  // ============================================================================

  /**
   * Get all user skill levels
   */
  async getUserSkills(): Promise<UserSkills> {
    const skills = await settingsStorage.get<UserSkills>('userSkills');
    return (skills as UserSkills) || {};
  }

  /**
   * Get a specific skill level (1-5)
   */
  async getSkillLevel(skillName: string): Promise<number> {
    const skills = await this.getUserSkills();
    return skills[skillName] ?? 0; // 0 means no skill trained
  }

  /**
   * Set a specific skill level (1-5)
   */
  async setSkillLevel(skillName: string, level: number): Promise<void> {
    if (level < 0 || level > 5) {
      throw new Error('Skill level must be between 0 and 5');
    }

    const skills = await this.getUserSkills();
    skills[skillName] = level;
    await settingsStorage.set('userSkills', skills);
  }

  /**
   * Set multiple skill levels at once
   * Note: This REPLACES all skills, not merges. To remove a skill, simply don't include it.
   */
  async setSkillLevels(skills: UserSkills): Promise<void> {
    // Validate all levels
    for (const [skillName, level] of Object.entries(skills)) {
      if (level < 0 || level > 5) {
        throw new Error(`Invalid level for skill ${skillName}: ${level}`);
      }
    }

    // Replace the entire skills object (don't merge with existing)
    await settingsStorage.set('userSkills', skills);
  }

  /**
   * Remove a skill
   */
  async removeSkill(skillName: string): Promise<void> {
    const skills = await this.getUserSkills();
    delete skills[skillName];
    await settingsStorage.set('userSkills', skills);
  }

  /**
   * Clear all user skills
   */
  async clearUserSkills(): Promise<void> {
    await settingsStorage.remove('userSkills');
  }

  // ============================================================================
  // Structure Bonuses
  // ============================================================================

  /**
   * Get structure/citadel bonuses
   */
  async getStructureBonuses(): Promise<StructureBonuses> {
    const bonuses = await settingsStorage.get<StructureBonuses>('structureBonuses');
    return (bonuses as StructureBonuses) || { structures: [] };
  }

  /**
   * Set structure/citadel bonuses
   */
  async setStructureBonuses(bonuses: StructureBonuses): Promise<void> {
    // Validate structure bonuses
    for (const structure of bonuses.structures) {
      if (structure.meBonus < 0 || structure.meBonus > 100) {
        throw new Error(`Invalid ME bonus: ${structure.meBonus}. Must be between 0 and 100.`);
      }
    }

    await settingsStorage.set('structureBonuses', bonuses);
  }

  /**
   * Clear all structure bonuses
   */
  async clearStructureBonuses(): Promise<void> {
    await settingsStorage.remove('structureBonuses');
  }

  // ============================================================================
  // App Settings
  // ============================================================================

  /**
   * Get application settings
   */
  async getAppSettings(): Promise<AppSettings> {
    const settings = await settingsStorage.get<AppSettings>('appSettings');
    return { ...DEFAULT_SETTINGS, ...(settings || {}) };
  }

  /**
   * Set application settings
   */
  async setAppSettings(settings: Partial<AppSettings>): Promise<void> {
    const current = await this.getAppSettings();
    const updated = { ...current, ...settings };
    await settingsStorage.set('appSettings', updated);
  }

  /**
   * Reset application settings to defaults
   */
  async resetAppSettings(): Promise<void> {
    await settingsStorage.set('appSettings', DEFAULT_SETTINGS);
  }

  /**
   * Get a specific setting value
   */
  async getSetting<K extends keyof AppSettings>(key: K): Promise<AppSettings[K]> {
    const settings = await this.getAppSettings();
    return settings[key];
  }

  /**
   * Set a specific setting value
   */
  async setSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): Promise<void> {
    const settings = await this.getAppSettings();
    settings[key] = value;
    await settingsStorage.set('appSettings', settings);
  }

  // ============================================================================
  // Clear All
  // ============================================================================

  /**
   * Clear all settings and user data
   */
  async clearAll(): Promise<void> {
    await settingsStorage.clear();
  }
}

/**
 * Singleton instance
 */
export const settingsCache = new SettingsCacheService();
